import {
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  BannerContainerVm,
  BannerVm,
  ContainerVm,
  ResourceVm,
} from 'qp-ui-interface';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { AppEventName } from 'src/app/analytics/newrelic/enums/EventName';
import { LanguageService } from 'src/app/language.service';
import { Tags } from 'src/app/shared/common/interfaces/display-info';
import { Destroyable } from 'src/app/shared/services/destroyable';
import { UserEntitlementService } from 'src/app/shared/services/evergent/entitlement/user-entitlement.service';
import { LocalStorageService } from 'src/app/shared/services/storage/local-storage.service';
import { SessionStorageService } from 'src/app/shared/services/storage/session-storage.service';
import {
  ContentDetailInfo,
  ContentDetailService,
} from 'src/app/shared/shared-components/content-detail/content-detail.service';
import { KEYS } from 'src/app/shared/utils/constants/storage.constants';
import { directToPlayer } from 'src/app/shared/utils/playerUtils';
import { ConfigService } from '../../shared/services/config.service';
import { NavigationService } from '../../shared/services/navigation.service';
import { NavigationLibraryService } from '../../shared/services/navigation/navigation.service';
import {
  PlayerEventType,
  SimplePlayerService,
} from '../../shared/shared-components/simple-player/simple-player.service';
import { CatalogService } from '../catalog.service';
import { NewrelicService } from './../../analytics/newrelic/services/newrelic.service';
import { DataService } from './../../data.service';
import { LocalStorageKeys } from 'src/app/shared/LocalStorageKeys';
import { RouteHistoryService } from 'src/app/shared/services/router-history.service';
import { IN_APP_BANNER_ID } from 'src/app/shared/common/const/common.const';

@Component({
  selector: 'app-collection-storefront',
  templateUrl: './collection-storefront.component.html',
  styleUrls: ['./collection-storefront.component.scss'],
})
export class CollectionStorefrontComponent
  extends Destroyable
  implements OnInit, OnDestroy
{
  public static classState = 'StorefrontComponent';
  @ViewChild('legacyCarouselTrack', { static: false })
  legacyCarouselTrack: ElementRef;
  @ViewChild('legacyCarouselWrapper', { static: false })
  legacyCarouselWrapper: ElementRef;
  @Input() lang = 'en';
  selectedTabId!: string;
  containers: ContainerVm[];
  isLoading = true;
  tempContainers: ContainerVm[] = [];
  isHeroFocused = true;
  currentResource: ResourceVm;
  contentDetailInfo: ContentDetailInfo;
  saved = false;
  isHeroImageLoaded = false;
  inAppBannerData: ContainerVm;
  canFocus: boolean = false;

  // Carousel Properties
  public carouselCurrentIndex = 0;
  public scrollDirection: 'left' | 'right' = 'right';
  private isAnimating = false;
  private animationDuration = 300;
  private scrollTimeout: any;

  VIEW_ALL_INDEX = 20;

  private readonly pageSize = 3;
  private pageNumber = 1;
  private maxContainers = 50;
  private loadingContainers = true;
  private autoScrollInterval: any;
  private isComponentVisible = false;
  private autoScrollSubscription: any; // Track auto-scroll subscription
  private currentVerticalIndex: number = 0;
  public carouselResources: ResourceVm[] | undefined;
  public isLegacyChromium: boolean = false;
  hasHero: boolean = true;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private dataService: DataService,
    private navigationService: NavigationService,
    private sessionStorage: SessionStorageService,
    private navService: NavigationLibraryService,
    private newrelic: NewrelicService,
    private readonly config: ConfigService,
    private catalogService: CatalogService,
    private contentDetailService: ContentDetailService,
    private simplePlayerService: SimplePlayerService,
    private localStorage: LocalStorageService,
    private userEntitlementService: UserEntitlementService,
    private readonly languageService: LanguageService,
    private readonly routeHistoryService: RouteHistoryService
  ) {
    super();
  }

  get titleImage(): string {
    return (
      this.currentResource?.heroTitleImage ||
      this.currentResource?.customTitleImage
    );
  }

  customTagFontSize(tag: Tags): string {
    return tag.key === null && this.isHeroFocused
      ? 'fontSize.21'
      : 'fontSize.18';
  }

  ngOnInit(): void {
    this.dataService.storefrontLastIndex = {
      hIndex: this.dataService.horizontalIndex,
      vIndex: this.dataService.verticalIndex,
    };
    this.dataService.emitVerticalMenuEvent({
      action: 'hide',
    });
    this.setupComponentState();
    this.setupSubscriptions();
    this.setupAutoScroll();

    this.route.params.pipe(takeUntil(this.destroy$)).subscribe(() => {
      // Read resolver data
      const { containers, totalContainers, isCollection } =
        this.route.snapshot.data?.storefrontData;

      const filterCarouselContainer =
        containers !== undefined
          ? containers.find((container) => container?.layout === 'carousel')
          : undefined;
      if (!filterCarouselContainer) {
        this.hasHero = false;
        this.currentResource = containers[0]?.resources[0];
        this.navService.focusContainer('horizontal-carousel-cards-0', true, 0);
      }

      this.dataService.currentResourceContainerName = '';
      this.dataService.currentResourceContainerId = '';
      this.navigationService.searchItemPosition = undefined;
      this.dataService.currentResourcePositionInTray = undefined;
      this.reset();

      if (containers?.length > 0 && totalContainers !== undefined) {
        this.maxContainers = totalContainers;
        this.setCarousels(totalContainers, [].concat(containers));
      }
    });
    this.navService.focusContainer('carousel-track', true, 0)
  }

  private resetDestroySubject(): void {
    if ((this as any).destroy$) {
      this.cleanup();
    }
    (this as any).destroy$ = new Subject<void>();
  }

  private setupSubscriptions(): void {
    this.setupComponentVisibilitySubscription();
    this.setupCurrentResourceSubscription();
  }

  private setupComponentVisibilitySubscription(): void {
    this.dataService.currentComponent
      .pipe(takeUntil(this.destroy$))
      .subscribe((component) => {
        this.isComponentVisible = component === CollectionStorefrontComponent;
        if (!this.isComponentVisible) {
          this.clearAutoScrollTimer();
        }
      });
  }

  private setupCurrentResourceSubscription(): void {
    this.dataService.currentResource
      .pipe(takeUntil(this.destroy$))
      .subscribe((resource) => {
        if (!resource) return;

        if (this.currentResource?.id !== resource.id) {
          this.resetHeroImage();
          this.currentResource = resource;
        }

        this.updateResourceContentDetail();
      });
  }

  private resetHeroImage(): void {
    const heroTitleImage = document.getElementById(
      'heroTitleImage'
    ) as HTMLImageElement;
    if (heroTitleImage) {
      heroTitleImage.src = '';
    }
    this.isHeroImageLoaded = false;
  }

  private updateResourceContentDetail(): void {
    if (this.isHeroFocused) {
      this.currentResource.heroTitleImage =
        this.contentDetailService.getOptimizedImageUrl(
          this.currentResource.heroTitleImage
        );
      this.contentDetailInfo = this.contentDetailService.getContentDetailInfo(
        this.currentResource,
        this.isHeroFocused
      );
    } else {
      this.currentResource.customTitleImage =
        this.contentDetailService.getOptimizedImageUrl(
          this.currentResource.customTitleImage
        );
      this.contentDetailInfo = null;
    }
  }

  /**
   * Setup component state and initialization that needs to be restored when component is reused
   */
  private setupComponentState(): void {
    this.lang = this.languageService.currLang;
    this.isLegacyChromium = this.detectLegacyChromium();
  }

  /**
   * Detect if running on legacy Chromium version
   */
  private detectLegacyChromium(): boolean {
    const raw = navigator.userAgent.match(/Chrom(?:e|ium)\/([0-9]+)\./);
    const chromiumVersion = raw ? parseInt(raw[1], 10) : null;

    if (chromiumVersion !== null && chromiumVersion <= 38) {
      return true;
    }

    const ua = navigator.userAgent;
    if (ua.includes('Web0S')) {
      const chrome = ua.match(/Chrome\/([0-9]+)/);
      const chromeVersion = chrome ? parseInt(chrome[1], 10) : null;
      const webOSVersion = window.webOsVersion || '';

      return (
        (chromeVersion === 38 || chromeVersion === 39) &&
        webOSVersion.startsWith('3.')
      );
    }

    return false;
  }

  ngOnDestroy(): void {
    this.resetStorefrontLastIndex();
    this.cleanup();
    this.clearAutoScrollTimer();
    this.clearAutoScrollSubscription();
    this.fullReset();
  }

  /**
   * Called by reuse strategy before save the component
   */
  stop(): void {
    this.dataService.horizontalIndex = this.navService.getHorizontalIndex();
    this.dataService.verticalIndex = this.currentVerticalIndex;

    // Save scroll position to prevent unwanted scrolling on reload
    const carouselContainer = document.querySelector(
      '.storefront-carousel-container'
    ) as HTMLElement;
    if (carouselContainer) {
      this.dataService.collectionStorefrontScrollPosition = {
        scrollTop: carouselContainer.scrollTop,
        scrollLeft: carouselContainer.scrollLeft,
      };
    }
  }

  reload(): void {
    setTimeout(() => {
      this.dataService.emitVerticalMenuEvent({
        action: 'hide',
      });
    }, 0);
    this.resetDestroySubject();
    this.setupSubscriptions();
    this.setupAutoScroll();
    this.setupComponentState();

    if (this.localStorage.getItem(LocalStorageKeys.TAB_CHANGED) === 'true') {
      this.isHeroFocused = true;
      this.clearScrollPosition();
    }

    if (this.isHeroFocused) {
      this.focusHeroCarousel();
    } else {
      this.setReloadFocus();
    }
  }

  private focusHeroCarousel(): void {
    this.navService.focusContainer('carousel-track', true, 0);
    requestAnimationFrame(() => {
      this.currentResource = this.carouselResources[this.carouselCurrentIndex];
      if (!this.isResourcePlayable(this.currentResource)) {
        this.simplePlayerService.resetEvent();
      }
      this.dataService.selectedCardData = this.currentResource;
    });
  }

  private isResourcePlayable(resource: ResourceVm): boolean {
    return resource.ape ? !!resource.apUrl?.dash : false;
  }

  setReloadFocus() {
    if (!this.isHeroFocused) {
      this.clearAutoScrollTimer();
      this.canFocus = true;
      setTimeout(() => {
        this.focusHorizontalCarousel(
          this.dataService.verticalIndex,
          this.dataService.horizontalIndex
        );
      }, 0);
      setTimeout(() => {
        this.canFocus = false;
      }, 700);
    }

    requestAnimationFrame(() => {
      const carouselContainer = document.querySelector(
        '.storefront-carousel-container'
      ) as HTMLElement;
      if (carouselContainer && this.dataService.storefrontScrollPosition) {
        carouselContainer.scrollTop =
          this.dataService.storefrontScrollPosition.scrollTop;
      }
    });
  }

  private clearScrollPosition(): void {
    this.dataService.storefrontScrollPosition = {
      scrollTop: 0,
      scrollLeft: 0,
    };
  }

  focusHorizontalCarousel(
    verticalIndex: number,
    horizontalIndex: number
  ): void {
    this.navService.setIndexContainer(
      `horizontal-carousel-cards-${verticalIndex}`,
      horizontalIndex
    );
    this.navService.focusContainer(
      `horizontal-carousel-cards-${verticalIndex}`,
      true,
      horizontalIndex
    );
  }

  async managePagination(): Promise<void> {
    if (
      this.pageNumber * this.pageSize < this.maxContainers &&
      !this.loadingContainers
    ) {
      try {
        this.pageNumber += 1;
        const { containers, totalContainers } =
          await this.catalogService.getCollectionStorefrontData(
            this.route.snapshot.params.id,
            this.pageSize,
            this.pageNumber
          );
        await this.setCarousels(totalContainers, containers);
      } catch (e) {
        console.log(e);
      }
    }
  }

  onArrowUp(index: number): void {
    if (index !== 0 || !this.hasHero) return;
    setTimeout(() => {
      this.currentResource = this.carouselResources[this.carouselCurrentIndex];
      this.heroFocused(true);
      this.dataService.selectedCardData = this.currentResource;
      this.navService.focusContainer('carousel-track', true, 0);
    }, 0);
  }

  heroFocused(isFocused: boolean): void {
    if (!isFocused) {
      this.clearAutoScrollTimer();
    } else {
      this.dataService.horizontalIndex = null;
      this.dataService.verticalIndex = null;
      this.clearScrollPosition();
    }
    this.isHeroFocused = isFocused;
  }

  private async setCarousels(
    totalContainers: number,
    containers: ContainerVm[]
  ): Promise<void> {
    this.maxContainers = totalContainers;
    this.containers = containers;

    this.setupCarouselResources(containers);
    this.setupCardContainers(containers);
    this.loadingContainers = false;

    this.triggerPaginationIfNeeded();
  }

  private setupCarouselResources(containers: ContainerVm[]): void {
    const carouselContainer = containers?.find(
      (container) => container?.layout === 'carousel'
    );

    if (!carouselContainer) return;

    const resources = carouselContainer.resources || [];
    resources.forEach((resource) => (resource.containerName = { en: 'hero' }));

    // Duplicate cards if less than 6 for better carousel experience
    this.carouselResources =
      resources.length > 0 && resources.length < 6
        ? [...resources, ...resources]
        : resources;

    this.dataService.selectedCardData = this.carouselResources[0];
    this.heroFocused(true);

    // Start auto-scroll on initial load after carousel is set up
    this.startInitialAutoScroll();
  }

  private startInitialAutoScroll(): void {
    // Start auto-scroll after a delay to ensure component is visible and hero is focused
    setTimeout(() => {
      if (
        this.isComponentVisible &&
        this.isHeroFocused &&
        this.carouselResources?.length > 0
      ) {
        this.startAutoScrollInterval();
      }
    }, 5000);
  }

  private setupCardContainers(containers: ContainerVm[]): void {
    const cardContainers = containers?.filter(
      (container) => container?.layout === 'card'
    );
    const processedContainers = this.cleanCarouselContainer(
      this.addViewAllResource(cardContainers) || []
    );
    this.tempContainers.push(...processedContainers);
  }

  private triggerPaginationIfNeeded(): void {
    if (!this.tempContainers?.length || this.tempContainers.length === 1) {
      setTimeout(() => this.managePagination(), 0);
    }
  }

  private cleanCarouselContainer(tempContainers: ContainerVm[]): ContainerVm[] {
    return tempContainers.filter(
      (e) =>
        e?.resources?.length > 0 ||
        this.config.applicationConfig?.personalisedName
          ?.split(',')
          .includes(e?.sourceType)
    );
  }

  private addViewAllResource(
    containers: ContainerVm[]
  ): ContainerVm[] | undefined {
    return containers;
  }

  private reset(): void {
    this.carouselCurrentIndex = 0;
    this.containers = undefined;
    this.tempContainers = [];
    this.pageNumber = 1;
  }

  private fullReset(): void {
    // Reset all stateful properties to their initial values
    this.carouselCurrentIndex = 0;
    this.scrollDirection = 'right';
    this.isAnimating = false;
    this.animationDuration = 300;
    this.selectedTabId = undefined;
    this.containers = undefined;
    this.tempContainers = [];
    this.isHeroFocused = true;
    this.currentResource = undefined;
    this.contentDetailInfo = undefined;
    this.saved = false;
    this.isHeroImageLoaded = false;
    this.inAppBannerData = undefined;
    this.pageNumber = 1;
    this.maxContainers = 50;
    this.loadingContainers = true;
    this.autoScrollInterval = null;
    this.autoScrollSubscription = null;
    this.isComponentVisible = false;
    this.currentVerticalIndex = 0;
    this.carouselResources = undefined;
    this.isLegacyChromium = false;

    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = null;
    }
    this.legacyCarouselTrack = null;
    this.legacyCarouselWrapper = null;
  }

  trackById(item: ResourceVm) {
    return item.id;
  }

  trackByIndex(index: number): number {
    return index;
  }

  trackByTagKey(index: number, tag: any): string {
    return tag?.key || index;
  }

  onHeroKeyDown(event: KeyboardEvent): void {
    setTimeout(() => {
      if (event.isDown()) {
        this.handleHeroKeyDown();
      } else if (event.isLeft()) {
        this.handleHorizontalScroll(true);
      } else if (event.isRight()) {
        this.handleHorizontalScroll(false);
      }
    }, 0);
  }

  private handleHeroKeyDown(): void {
    this.heroFocused(false);
    if (this.contentDetailInfo) {
      this.contentDetailInfo.customTags =
        this.contentDetailService.refreshCustomTags(
          this.currentResource,
          this.isHeroFocused
        );
    }
  }

  isCardFocused(container: ContainerVm, index: number): void {
    this.isHeroFocused = false;
    this.scrollToContainer(container.id);
    this.navService.setVerticalIndex(index);
    this.currentVerticalIndex = index;
  }

  private scrollToContainer(containerId: string): void {
    const header = document.getElementById(containerId);
    if (!header) return;

    const carouselContainer = document.querySelector(
      '.storefront-carousel-container'
    ) as HTMLElement;
    if (!carouselContainer) return;

    const headerRect = header.getBoundingClientRect();
    const containerRect = carouselContainer.getBoundingClientRect();
    const scrollTop =
      headerRect.top - containerRect.top + carouselContainer.scrollTop;
    carouselContainer.scrollTop = scrollTop;
  }

  onClick() {
    if (!this.isHeroFocused) return;
    this.clearAutoScrollTimer();
    const resource = this.currentResource;
    this.dataService.selectedCardData = resource;
    const { type, id: contentID } = resource;
    if (directToPlayer(resource)) {
      this.router.navigate([`player/${type}/${contentID}`], {
        state: { type, id: contentID },
      });
    } else {
      this.router.navigate([`/${resource.contentType}/${resource.uniqueId}`]);
    }
  }

  private setupAutoScroll() {
    this.clearAutoScrollTimer();
    this.clearAutoScrollSubscription();

    // Collection storefront doesn't use selectedTabId, so remove that filter
    this.autoScrollSubscription = this.simplePlayerService.playerEvents$
      .pipe(
        takeUntil(this.destroy$),
        filter((event) =>
          [
            PlayerEventType.VIDEO_AVAILABLE,
            PlayerEventType.VIDEO_COMPLETED,
            PlayerEventType.FORCE_DESTROY,
          ].includes(event.type)
        )
      )
      .subscribe((event) => {
        switch (event.type) {
          case PlayerEventType.VIDEO_AVAILABLE:
            this.clearAutoScrollTimer();
            if (
              this.isComponentVisible &&
              event.data === false &&
              this.isHeroFocused
            ) {
              this.startAutoScrollInterval();
            }
            break;
          case PlayerEventType.VIDEO_COMPLETED:
            this.clearAutoScrollTimer();
            if (this.isComponentVisible && this.isHeroFocused) {
              this.autoScroll();
            }
            break;
          case PlayerEventType.FORCE_DESTROY:
            this.clearAutoScrollTimer();
            break;
        }
      });
  }

  private startAutoScrollInterval(): void {
    this.clearAutoScrollTimer();
    this.autoScrollInterval = setInterval(() => {
      if (this.isComponentVisible && this.isHeroFocused) {
        this.autoScroll();
      }
    }, 5000);
  }

  private autoScroll() {
    // Validate state before proceeding
    if (!this.isComponentVisible || !this.isHeroFocused) {
      return;
    }

    this.clearAutoScrollTimer();
    if (this.isLegacyChromium) {
      this.scrollLegacyCarouselToActive();
    } else {
      this.handleHorizontalScroll();
    }
  }

  private clearAutoScrollTimer() {
    if (this.autoScrollInterval) {
      clearInterval(this.autoScrollInterval);
      this.autoScrollInterval = null;
    }
  }

  private clearAutoScrollSubscription() {
    if (this.autoScrollSubscription) {
      this.autoScrollSubscription.unsubscribe();
      this.autoScrollSubscription = null;
    }
  }

  onImageLoad(event: Event) {
    const imgElement = event.target as HTMLImageElement;
    if (imgElement.complete) {
      this.isHeroImageLoaded = true;
    }
  }

  // Banner logic
  setInAppBannerData(container: BannerContainerVm) {
    if (this.inAppBannerData) {
      return null;
    }
    const bannerContainer: ContainerVm = {
      id: IN_APP_BANNER_ID,
      name: container.name,
      layout: 'card',
      resources: [],
    };
    try {
      const userType = this.userEntitlementService.bannerSubscriptionStatus();
      const banner = container.banners.find((b) => b.type === userType);
      bannerContainer.resources = this.inAppBannerAdapter(container, banner);
      this.inAppBannerData = bannerContainer;
      return bannerContainer;
    } catch (error) {
      console.error('Error in setInAppBannerData:', error);
    }
    return null;
  }

  getImageUrl(value: string): string {
    return `${value}?width=2560`;
  }

  private inAppBannerAdapter(
    container: BannerContainerVm,
    banner: BannerVm
  ): ResourceVm[] | null {
    try {
      const bannerImage = this.getImageUrl(banner.bannerImage);
      const providerImage = this.getImageUrl(banner.providerImage);

      return [
        {
          id: IN_APP_BANNER_ID,
          key: '',
          name: container.name.en,
          type: '',
          title: container.name,
          contentType: '',
          trailers: [],
          keywords: [],
          bannerImage,
          apUrl: null,
          ape: false,
          shortDescription: null,
          providerImage,
        },
      ];
    } catch (error) {
      console.error('Error in bannerAdapter:', error);
      return null;
    }
  }

  trackByContainerId(container: any): any {
    return container.id;
  }

  handleHorizontalScroll(invert: boolean = false): void {
    if (this.isAnimating) return;
    // Double-check focus state to ensure carousel never moves when focus is out
    if (!this.isHeroFocused || !this.carouselResources?.length) return;

    const resources = this.carouselResources;
    const total = resources.length;

    this.scrollDirection = invert ? 'left' : 'right';
    this.isAnimating = true;

    if (!invert) {
      // Move right
      this.carouselCurrentIndex = (this.carouselCurrentIndex + 1) % total;
    } else {
      // Move left
      this.carouselCurrentIndex =
        (this.carouselCurrentIndex - 1 + total) % total;
    }

    this.scrollTimeout = setTimeout(() => {
      this.isAnimating = false;
      this.scrollTimeout = null;
    }, this.animationDuration);

    // this.currentResource = resources[this.carouselCurrentIndex];
    this.dataService.selectedCardData = resources[this.carouselCurrentIndex];
    this.navService.setIndexContainer('container-all', 0);
  }

  onCarouselKeydown(event: KeyboardEvent): void {
    if (event.isDown()) {
      this.managePagination();
    }
  }

  private scrollLegacyCarouselToActive(): void {
    this.carouselCurrentIndex =
      (this.carouselCurrentIndex + 1) % this.carouselResources.length;

    if (
      !this.legacyCarouselTrack ||
      !this.legacyCarouselWrapper ||
      this.carouselCurrentIndex == null
    ) {
      return;
    }

    const track = this.legacyCarouselTrack.nativeElement as HTMLElement;
    const wrapper = this.legacyCarouselWrapper.nativeElement as HTMLElement;
    const cards = track.querySelectorAll('.carousel-item');
    const activeCard = cards[this.carouselCurrentIndex] as HTMLElement;

    if (activeCard) {
      const scrollTo =
        activeCard.offsetLeft -
        wrapper.offsetWidth / 2 +
        activeCard.offsetWidth / 2;
      wrapper.scrollLeft = scrollTo;
    }
  }

  onBack(): void {
    this.routeHistoryService.goBack();
  }

  resetStorefrontLastIndex() {
    this.dataService.verticalIndex =
      this.dataService.storefrontLastIndex.vIndex;
    this.dataService.horizontalIndex =
      this.dataService.storefrontLastIndex.hIndex;
    this.dataService.collectionStorefrontScrollPosition = null;
    this.dataService.emitVerticalMenuEvent({
      action: 'show',
    });
  }
}
